<template>
  <page-wrapper title="Make Payment">
    <div class="d-flex mb-4">
      <div style="flex: 0 0 auto;max-width:100%">
        <v-card class="ma-0 py-2 px-3">
          <breadcrumbs :steps="steps" v-model="stepID"/>
        </v-card>
      </div>
    </div>

    <v-row>
      <v-col md="8">
        <validation-error-panel/>
        <workflow-wrapper>
          <step-case-search v-if="stepID === 'caseSearch'"
                            key="caseSearch"
                            :originalBean="bean"
                            @save="saveCaseSearch"/>
          <step-select-case v-else-if="stepID === 'selectCase'"
                            key="selectCase"
                            :originalBean="bean"
                            @save="saveSelectCase"/>
          <step-select-party v-else-if="stepID === 'selectParty'"
                             key="selectParty"
                             :originalBean="bean"
                             @save="saveSelectParty"/>
          <step-verification v-else-if="stepID === 'verification'"
                             key="verification"
                             :originalBean="bean"
                             @save="saveVerification"/>
          <step-payment-option v-else-if="stepID === 'paymentOption'"
                               key="paymentOption"
                               :originalBean="bean"
                               @save="savePaymentOption"/>
          <step-summary v-else-if="stepID === 'summary'"
                        key="summary"
                        :originalBean="bean"/>
        </workflow-wrapper>
      </v-col>
    </v-row>
  </page-wrapper>
</template>

<script>
import _ from 'lodash';
import PageMixin from '@/mixins/page';
import PageWrapper from '@components/support/PageWrapper';
import Breadcrumbs from '@components/support/Breadcrumbs';
import WorkflowWrapper from '@components/support/WorkflowWrapper';
import ValidationErrorPanel from '@components/support/ValidationErrorPanel';
import StepCaseSearch from '@components/support/makePayment/StepCaseSearch';
import StepSelectCase from '@components/support/makePayment/StepSelectCase';
import StepSelectParty from '@components/support/makePayment/StepSelectParty';
import StepVerification from '@components/support/makePayment/StepVerification';
import StepPaymentOption from '@components/support/makePayment/StepPaymentOption';
import StepSummary from '@components/support/makePayment/StepSummary';

export default {
  components: { PageWrapper, Breadcrumbs, WorkflowWrapper, ValidationErrorPanel, StepCaseSearch, StepSelectCase,
    StepSelectParty, StepVerification, StepPaymentOption, StepSummary },
  mixins: [ PageMixin ],
  props: {
    inputData: {
      type: Object
    }
  },
  data () {
    return {
      stepID: 'caseSearch',
      bean: {
        caseSearchCriteria: {
          searchType: 'caseNumber',
          caseNumber: null,
          citationNumber: null,
          cid: null,
          vehiclePlateNumber: null,
          driversLicenseNumber: null
        },
        verificationCriteria: {
          entryType: 'cid',
          cid: null,
          dateOfBirth: null,
          driversLicenseNumber: null
        },
        selections: {
          caseInstanceUUID: null,
          casePartyUUID: null,
          paymentOptionID: null,
          amount: null
        },
        caseSearchResults: [],
        partySearchResults: [],
        paymentOptionResults: [],
        isVerifiedCaseParty: false,
        verificationNotRequired: false
      }
    };
  },
  created () {
    _.merge(this.$data, this.inputData);
  },
  computed: {
    steps () {
      let steps = [];
      steps.push({ stepID: 'caseSearch', name: this.$t('page.makePayment.step.caseSearch') });
      steps.push({ stepID: 'selectCase', name: this.$t('page.makePayment.step.selectCase') });
      steps.push({ stepID: 'selectParty', name: this.$t('page.makePayment.step.selectParty') });
      steps.push({ stepID: 'verification', name: this.$t('page.makePayment.step.verification') });
      steps.push({ stepID: 'paymentOption', name: this.$t('page.makePayment.step.paymentOption') });
      steps.push({ stepID: 'summary', name: this.$t('page.makePayment.step.summary') });

      return steps;
    }
  },
  methods: {
    saveCaseSearch (bean) {
      this.bean = bean;

      // reset data for future steps
      this.bean.verificationCriteria.entryType = 'cid';
      this.bean.verificationCriteria.cid = null;
      this.bean.verificationCriteria.dateOfBirth = null;
      this.bean.verificationCriteria.driversLicenseNumber = null;
      this.bean.selections.casePartyUUID = null;
      this.bean.selections.paymentOptionID = null;
      this.bean.selections.amount = null;
      this.bean.partySearchResults = [];
      this.bean.paymentOptionResults = [];
      this.bean.isVerifiedCaseParty = false;
      this.bean.verificationNotRequired = false;

      this.stepID = 'selectCase';
    },
    saveSelectCase (bean) {
      this.bean = bean;

      // reset data for future steps
      this.bean.verificationCriteria.entryType = 'cid';
      this.bean.verificationCriteria.cid = null;
      this.bean.verificationCriteria.dateOfBirth = null;
      this.bean.verificationCriteria.driversLicenseNumber = null;
      this.bean.selections.casePartyUUID = null;
      this.bean.selections.paymentOptionID = null;
      this.bean.selections.amount = null;
      this.bean.partySearchResults = [];
      this.bean.paymentOptionResults = [];
      this.bean.isVerifiedCaseParty = false;
      this.bean.verificationNotRequired = false;

      this.stepID = 'selectParty';
    },
    saveSelectParty (bean) {
      this.bean = bean;

      // reset data for future steps
      this.bean.verificationCriteria.entryType = 'cid';
      this.bean.verificationCriteria.cid = null;
      this.bean.verificationCriteria.dateOfBirth = null;
      this.bean.verificationCriteria.driversLicenseNumber = null;
      this.bean.selections.paymentOptionID = null;
      this.bean.selections.amount = null;
      this.bean.paymentOptionResults = [];

      let selectedParty = _.find(this.bean.partySearchResults, ['casePartyUUID', this.bean.selections.casePartyUUID]);
      if (this.bean.caseSearchCriteria.searchType === 'driversLicenseNumber' ||
          this.bean.caseSearchCriteria.searchType === 'cid' ||
          this.bean.isVerifiedCaseParty ||
          !selectedParty.includesNotRequiredAssessment) {
        this.bean.verificationNotRequired = true;
        this.stepID = 'paymentOption';
      } else {
        this.stepID = 'verification';
      }
    },
    saveVerification (bean) {
      this.bean = bean;

      // reset data for future steps
      this.bean.selections.paymentOptionID = null;
      this.bean.selections.amount = null;
      this.bean.paymentOptionResults = [];

      this.stepID = 'paymentOption';
    },
    savePaymentOption (bean) {
      this.bean = bean;
      this.stepID = 'summary';
    }
  }
};
</script>